/**
 * Keep table action dropdowns visible when panel/table overflow would clip them.
 * Mirrors PeriodDatepickerFilter: force position:fixed to the viewport on open.
 */

const VIEWPORT_MARGIN = 8;
const ROOT_SELECTOR = 'table td.actions .btn-group, table .actions .btn-group';

/** @type {{ trigger: HTMLElement, menu: HTMLElement, root: HTMLElement } | null} */
let activeDropdown = null;

/**
 * @param {DOMRect} triggerRect
 * @param {number} menuWidth
 * @param {number} menuHeight
 * @param {number} viewportWidth
 * @param {number} viewportHeight
 * @param {number} [margin]
 * @returns {{ top: number, left: number }}
 */
export function computeTableDropdownPosition(
	triggerRect,
	menuWidth,
	menuHeight,
	viewportWidth,
	viewportHeight,
	margin = VIEWPORT_MARGIN,
) {
	const maxLeft = viewportWidth - margin - menuWidth;
	let left = triggerRect.right - menuWidth;
	if (maxLeft < margin) {
		left = margin;
	} else {
		left = Math.min(Math.max(margin, left), maxLeft);
	}

	const spaceBelow = viewportHeight - triggerRect.bottom - margin;
	const spaceAbove = triggerRect.top - margin;
	let top =
		spaceBelow >= menuHeight || spaceBelow >= spaceAbove
			? triggerRect.bottom
			: triggerRect.top - menuHeight;

	const maxTop = viewportHeight - margin - menuHeight;
	if (maxTop < margin) {
		top = margin;
	} else {
		top = Math.min(Math.max(margin, top), maxTop);
	}

	return { top, left };
}

/**
 * @param {HTMLElement} menu
 * @returns {{ width: number, height: number }}
 */
function measureMenu(menu) {
	const rect = menu.getBoundingClientRect();
	let width = rect.width;
	let height = rect.height;

	if (width < 4) {
		width = menu.offsetWidth;
	}
	if (height < 4) {
		height = menu.offsetHeight;
	}

	return {
		width: width < 4 ? 180 : width,
		height: height < 4 ? 120 : height,
	};
}

/**
 * @param {HTMLElement} trigger
 * @param {HTMLElement} menu
 */
function applyFixedPosition(trigger, menu) {
	const triggerRect = trigger.getBoundingClientRect();
	const { width, height } = measureMenu(menu);
	const { top, left } = computeTableDropdownPosition(
		triggerRect,
		width,
		height,
		window.innerWidth,
		window.innerHeight,
	);

	menu.style.setProperty('position', 'fixed', 'important');
	menu.style.setProperty('top', `${top}px`, 'important');
	menu.style.setProperty('left', `${left}px`, 'important');
	menu.style.setProperty('right', 'auto', 'important');
	menu.style.setProperty('transform', 'none', 'important');
	menu.style.setProperty('will-change', 'auto', 'important');
	menu.style.setProperty('z-index', '1300', 'important');
}

/**
 * @param {HTMLElement} menu
 */
function clearFixedPosition(menu) {
	menu.style.removeProperty('position');
	menu.style.removeProperty('top');
	menu.style.removeProperty('left');
	menu.style.removeProperty('right');
	menu.style.removeProperty('transform');
	menu.style.removeProperty('will-change');
	menu.style.removeProperty('z-index');
}

/**
 * @param {HTMLElement} root
 * @returns {{ trigger: HTMLElement, menu: HTMLElement } | null}
 */
function resolveDropdownParts(root) {
	const trigger = root.querySelector('[data-toggle="dropdown"]');
	const menu = root.querySelector('.dropdown-menu');

	if (!(trigger instanceof HTMLElement) || !(menu instanceof HTMLElement)) {
		return null;
	}

	return { trigger, menu };
}

function repositionActiveDropdown() {
	if (!activeDropdown) {
		return;
	}

	applyFixedPosition(activeDropdown.trigger, activeDropdown.menu);
}

/**
 * jQuery delegated handler: `this` is the matched .btn-group root.
 * @this {HTMLElement}
 */
function onDropdownShown() {
	const root = this;
	if (!(root instanceof HTMLElement)) {
		return;
	}

	const parts = resolveDropdownParts(root);
	if (!parts) {
		return;
	}

	activeDropdown = { root, trigger: parts.trigger, menu: parts.menu };
	applyFixedPosition(parts.trigger, parts.menu);
	requestAnimationFrame(() => {
		applyFixedPosition(parts.trigger, parts.menu);
		setTimeout(() => applyFixedPosition(parts.trigger, parts.menu), 0);
	});
}

/**
 * jQuery delegated handler: `this` is the matched .btn-group root.
 * @this {HTMLElement}
 */
function onDropdownHide() {
	const root = this;
	if (!(root instanceof HTMLElement)) {
		return;
	}

	const parts = resolveDropdownParts(root);
	if (parts) {
		clearFixedPosition(parts.menu);
	}

	if (activeDropdown?.root === root) {
		activeDropdown = null;
	}
}

export function initTableActionsDropdowns() {
	if (typeof window === 'undefined' || typeof window.jQuery === 'undefined') {
		return;
	}

	const $ = window.jQuery;

	$(document)
		.off('shown.bs.dropdown.tableActions', ROOT_SELECTOR)
		.off('hide.bs.dropdown.tableActions', ROOT_SELECTOR)
		.on('shown.bs.dropdown.tableActions', ROOT_SELECTOR, onDropdownShown)
		.on('hide.bs.dropdown.tableActions', ROOT_SELECTOR, onDropdownHide);

	window.removeEventListener('resize', repositionActiveDropdown);
	window.addEventListener('resize', repositionActiveDropdown);
	document.removeEventListener('scroll', repositionActiveDropdown, true);
	document.addEventListener('scroll', repositionActiveDropdown, true);
}
